import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Award, Lock, Star, Flame, Leaf, Droplets,
  TreeDeciduous, Globe, Gift, Recycle, Crown, Medal, Trophy, Eye, Loader2, AlertCircle
} from 'lucide-react';
import { BADGES_DATA as CURRICULUM_BADGES } from '../../data/curriculum';
import { CertificateModal } from '../../components/common';
import useAuthStore from '../../store/authStore';
import badgeService from '../../services/badgeService';
import progressService from '../../services/progressService';

// Icon mapping
const ICON_MAP = {
  leaf: Leaf,
  seedling: Leaf,
  droplets: Droplets,
  'tree-deciduous': TreeDeciduous,
  globe: Globe,
  star: Star,
  flame: Flame,
  trophy: Trophy,
  crown: Crown,
  medal: Medal,
  award: Award,
  gift: Gift,
  recycle: Recycle,
};

// Color mapping based on badge type
const COLOR_MAP = {
  achievement: 'bg-leaf-100 text-leaf-600',
  milestone: 'bg-sunshine-100 text-sunshine-600',
  consistency: 'bg-orange-100 text-orange-600',
  special: 'bg-purple-100 text-purple-600',
};

// Fallback mock earned badges
const MOCK_EARNED_BADGE_IDS = [1, 3, 10];

// Fallback certificates
const MOCK_CERTIFICATES = [];

// Calculate upcoming certificates based on student progress and database months
const getUpcomingCertificates = (currentWeek, earnedCertificates = [], months = []) => {
  const upcoming = [];
  const earnedTitles = earnedCertificates.map(c => c.title?.toLowerCase() || '');

  // Add monthly certificates from database months
  if (months.length > 0) {
    months.forEach((month) => {
      const certTitle = `${month.name} Completion Certificate`;
      const weeksNeeded = month.end_week;
      const weeksStarted = month.start_week;

      if (!earnedTitles.some(t => t.includes(month.name.toLowerCase()))) {
        upcoming.push({
          title: certTitle,
          description: `Complete Weeks ${month.start_week}-${month.end_week}`,
          weeksRequired: weeksNeeded,
          progress: Math.min(100, Math.round((Math.max(0, currentWeek - weeksStarted + 1) / (weeksNeeded - weeksStarted + 1)) * 100)),
          isNext: currentWeek >= weeksStarted && currentWeek <= weeksNeeded,
        });
      }
    });
  } else {
    // Fallback to hardcoded months if API fails
    const fallbackMonths = [
      { name: 'September', start_week: 1, end_week: 4 },
      { name: 'October', start_week: 5, end_week: 8 },
      { name: 'November', start_week: 9, end_week: 12 },
      { name: 'January', start_week: 13, end_week: 16 },
      { name: 'February', start_week: 17, end_week: 20 },
      { name: 'March', start_week: 21, end_week: 24 },
      { name: 'April', start_week: 25, end_week: 28 },
      { name: 'May', start_week: 29, end_week: 32 },
      { name: 'June', start_week: 33, end_week: 36 },
    ];

    fallbackMonths.forEach((month) => {
      const certTitle = `${month.name} Completion Certificate`;
      if (!earnedTitles.some(t => t.includes(month.name.toLowerCase()))) {
        upcoming.push({
          title: certTitle,
          description: `Complete Weeks ${month.start_week}-${month.end_week}`,
          weeksRequired: month.end_week,
          progress: Math.min(100, Math.round((currentWeek / month.end_week) * 100)),
          isNext: currentWeek >= month.start_week && currentWeek <= month.end_week,
        });
      }
    });
  }

  // Add term certificates
  const terms = [
    { name: 'First Term Certificate', weeksRequired: 12, startWeek: 1 },
    { name: 'Second Term Certificate', weeksRequired: 24, startWeek: 13 },
    { name: 'Third Term Certificate', weeksRequired: 36, startWeek: 25 },
  ];

  terms.forEach(term => {
    if (!earnedTitles.some(t => t.includes(term.name.toLowerCase()))) {
      upcoming.push({
        title: term.name,
        description: `Complete all ${term.weeksRequired} weeks`,
        weeksRequired: term.weeksRequired,
        progress: Math.min(100, Math.round((currentWeek / term.weeksRequired) * 100)),
        isNext: false,
      });
    }
  });

  // Add annual certificate
  if (!earnedTitles.some(t => t.includes('annual') || t.includes('green guardian'))) {
    upcoming.push({
      title: 'Annual Green Guardian Certificate',
      description: 'Complete all 36 weeks',
      weeksRequired: 36,
      progress: Math.min(100, Math.round((currentWeek / 36) * 100)),
      isNext: false,
    });
  }

  // Sort by weeks required and filter to show only relevant upcoming ones
  return upcoming
    .filter(c => c.progress < 100)
    .sort((a, b) => a.weeksRequired - b.weeksRequired)
    .slice(0, 4); // Show max 4 upcoming
};

export default function Badges() {
  const { student, user, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('badges');
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [badgesData, setBadgesData] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [months, setMonths] = useState([]);

  // Fetch badges, certificates, and months
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !student) {
        // Use mock data if not authenticated
        const mockBadges = CURRICULUM_BADGES.map((badge) => ({
          id: badge.id,
          name: badge.name,
          description: badge.description,
          icon: ICON_MAP[badge.icon] || Leaf,
          color: COLOR_MAP[badge.type] || 'bg-gray-100 text-gray-600',
          earned: MOCK_EARNED_BADGE_IDS.includes(badge.id),
          earnedAt: MOCK_EARNED_BADGE_IDS.includes(badge.id) ? '2026-01-20' : null,
          type: badge.type,
          progress: badge.type === 'consistency' && !MOCK_EARNED_BADGE_IDS.includes(badge.id)
            ? { current: Math.floor(Math.random() * (badge.criteria?.streak_days || 7)), target: badge.criteria?.streak_days || 7 }
            : null,
        }));
        setBadgesData(mockBadges);
        setCertificates(MOCK_CERTIFICATES);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch badges
        const badgesResponse = await badgeService.getMyBadges();

        // Combine earned and available badges
        const allBadges = [
          ...(badgesResponse.earned_badges || []).map((b) => ({
            ...b,
            earned: true,
          })),
          ...(badgesResponse.available_badges || []).map((b) => ({
            ...b,
            earned: false,
          })),
        ];

        // Transform to display format
        const transformed = allBadges.map((badge) => ({
          id: badge.id,
          name: badge.name,
          description: badge.description,
          icon: ICON_MAP[badge.icon] || Leaf,
          color: COLOR_MAP[badge.badge_type] || 'bg-gray-100 text-gray-600',
          earned: badge.earned,
          earnedAt: badge.earned_at,
          type: badge.badge_type,
          progress: null,
        }));

        setBadgesData(transformed);

        // Fetch certificates
        try {
          const certsResponse = await badgeService.getMyCertificates();
          setCertificates(certsResponse || []);
        } catch (certErr) {
          console.error('Failed to fetch certificates:', certErr);
          setCertificates([]);
        }

        // Fetch months for upcoming certificates
        try {
          const monthsResponse = await progressService.getMonths();
          setMonths(monthsResponse || []);
        } catch (monthsErr) {
          console.error('Failed to fetch months:', monthsErr);
          setMonths([]);
        }
      } catch (err) {
        console.error('Failed to fetch badges:', err);
        setError('Could not load badges. Using sample data.');

        // Fallback to curriculum badges
        const fallbackBadges = CURRICULUM_BADGES.map((badge) => ({
          id: badge.id,
          name: badge.name,
          description: badge.description,
          icon: ICON_MAP[badge.icon] || Leaf,
          color: COLOR_MAP[badge.type] || 'bg-gray-100 text-gray-600',
          earned: MOCK_EARNED_BADGE_IDS.includes(badge.id),
          earnedAt: MOCK_EARNED_BADGE_IDS.includes(badge.id) ? '2026-01-20' : null,
          type: badge.type,
          progress: null,
        }));
        setBadgesData(fallbackBadges);
        setCertificates(MOCK_CERTIFICATES);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, student]);

  const earnedCount = badgesData.filter((b) => b.earned).length;

  // Get student and school info for certificates
  const studentName = student?.name || 'Green Champion';
  const schoolName = student?.school_name || user?.school_name || 'Green Valley Primary';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-leaf-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading badges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl mb-2">Badges & Certificates</h1>
        <p className="text-gray-600">
          {earnedCount} of {badgesData.length} badges earned
        </p>
      </div>

      {/* Error Banner (non-blocking) */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-700 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Progress Ring */}
      <div className="flex justify-center mb-8">
        <div className="relative w-32 h-32">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="none"
            />
            <motion.circle
              cx="64"
              cy="64"
              r="56"
              stroke="#22c55e"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
              initial={{ strokeDasharray: '0 352' }}
              animate={{
                strokeDasharray: `${badgesData.length > 0 ? (earnedCount / badgesData.length) * 352 : 0} 352`,
              }}
              transition={{ duration: 1, delay: 0.3 }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-leaf-700">{earnedCount}</span>
            <span className="text-xs text-gray-500">earned</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('badges')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 ${
            activeTab === 'badges'
              ? 'bg-white text-leaf-700 shadow'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Badges
        </button>
        <button
          onClick={() => setActiveTab('certificates')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 ${
            activeTab === 'certificates'
              ? 'bg-white text-leaf-700 shadow'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Certificates
        </button>
      </div>

      {/* Badges Grid */}
      {activeTab === 'badges' && (
        <div className="grid grid-cols-2 gap-4">
          {badgesData.map((badge, index) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`card p-4 text-center ${
                  !badge.earned ? 'opacity-60' : ''
                }`}
              >
                {/* Badge Icon */}
                <div
                  className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center
                             ${badge.earned ? badge.color : 'bg-gray-100 text-gray-400'}`}
                >
                  {badge.earned ? (
                    <Icon size={28} />
                  ) : (
                    <Lock size={24} />
                  )}
                </div>

                {/* Badge Name */}
                <h3 className="font-semibold text-sm mb-1">{badge.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{badge.description}</p>

                {/* Status */}
                {badge.earned ? (
                  <span className="badge badge-success text-xs">
                    Earned {badge.earnedAt ? new Date(badge.earnedAt).toLocaleDateString() : ''}
                  </span>
                ) : badge.progress ? (
                  <div className="mt-2">
                    <div className="progress-bar h-2">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${(badge.progress.current / badge.progress.target) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {badge.progress.current}/{badge.progress.target}
                    </p>
                  </div>
                ) : (
                  <span className="badge badge-locked text-xs">
                    Locked
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Certificates */}
      {activeTab === 'certificates' && (
        <div className="space-y-4">
          {certificates.length > 0 ? (
            certificates.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-sunshine-100 flex items-center justify-center">
                    <Award size={28} className="text-sunshine-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{cert.title}</h3>
                    <p className="text-sm text-gray-500">
                      Issued on {new Date(cert.issued_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedCertificate(cert)}
                    className="btn-ghost py-2 px-4 flex items-center gap-2"
                  >
                    <Eye size={18} />
                    <span className="hidden sm:inline">View</span>
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <Award size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No certificates yet.</p>
              <p className="text-sm text-gray-400">
                Complete monthly goals to earn certificates!
              </p>
            </div>
          )}

          {/* Upcoming Certificates */}
          <div className="mt-8">
            <h3 className="font-heading text-lg text-leaf-800 mb-4">Upcoming Certificates</h3>
            <div className="space-y-3">
              {getUpcomingCertificates(student?.current_week || 1, certificates, months).map(
                (cert) => (
                  <div
                    key={cert.title}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      cert.isNext ? 'bg-leaf-50 border border-leaf-200' : 'bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      cert.isNext ? 'bg-leaf-200' : 'bg-gray-200'
                    }`}>
                      <Lock size={18} className={cert.isNext ? 'text-leaf-600' : 'text-gray-400'} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${cert.isNext ? 'text-leaf-800' : 'text-gray-600'}`}>
                        {cert.title}
                        {cert.isNext && <span className="ml-2 text-xs bg-leaf-500 text-white px-2 py-0.5 rounded-full">Next</span>}
                      </p>
                      <p className="text-xs text-gray-500">{cert.description}</p>
                      {/* Progress bar */}
                      <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${cert.isNext ? 'bg-leaf-500' : 'bg-gray-400'}`}
                          style={{ width: `${cert.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{cert.progress}% complete</p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      <CertificateModal
        isOpen={!!selectedCertificate}
        onClose={() => setSelectedCertificate(null)}
        studentName={studentName}
        schoolName={schoolName}
        achievement={selectedCertificate?.title || ''}
        date={selectedCertificate?.issued_at || new Date().toISOString()}
        type={selectedCertificate?.type || 'monthly'}
      />
    </div>
  );
}
